/** @param {string} text */
function p(text) {
  return { type: "paragraph", html: `<p>${text}</p>` };
}

/** @param {string} text */
function quote(text) {
  return { type: "quote", text };
}

/** @param {string} imageKey @param {string} caption */
function fig(imageKey, caption) {
  return {
    type: "figure",
    imageKey,
    caption: `[ ${caption} ]`,
  };
}

/** @param {string} left @param {string} right @param {string} caption */
function pair(left, right, caption) {
  return {
    type: "figurePair",
    leftImageKey: left,
    rightImageKey: right,
    caption: `[ ${caption} ]`,
  };
}

/** @param {string} videoId @param {string} [caption] */
function yt(videoId, caption) {
  return {
    type: "youtube",
    videoId,
    ...(caption ? { caption } : {}),
  };
}

const VISIT_COLLAGE = {
  type: "figureCollage",
  heading: "FROM THE VISIT",
  caption: "Personal moments from Ol Pejeta — less polished, more real.",
  images: [
    { imageKey: "cjmeister-com-IMG_0038.jpg" },
    { imageKey: "cjmeister-com-IMG_0069.jpg" },
    { imageKey: "cjmeister-com-IMG_0064.jpg" },
    { imageKey: "cjmeister-com-IMG_0048.jpg" },
    { imageKey: "cjmeister-com-IMG_0012.jpg" },
    { imageKey: "cjmeister-com-IMG_9988.jpg" },
    { imageKey: "cjmeister-com-IMG_9986.jpg" },
    { imageKey: "cjmeister-com-IMG_9983.jpg" },
    { imageKey: "cjmeister-com-IMG_9998.jpg" },
  ],
};

export const RHINO_STORY_META = {
  id: "rhino",
  cat: "Adventure & Travel",
  title: "Meeting the Last Two Northern White Rhinos",
  seoTitle:
    "Meeting the Last Two Northern White Rhinos — A Photo Story from Ol Pejeta, Kenya",
  img: "cjmeister-com-_CJ_1741-2.jpg",
  seoImage: "rhino-hero.jpg",
  imageAlt:
    "Northern white rhinos Najin and Fatu at Ol Pejeta Conservancy, Kenya — photo by CJ Jayamaha",
  date: "FEB 2022",
  publishedDate: "2022-02-01",
  read: "18 MIN READ",
  loc: "Ol Pejeta, Kenya",
  dek: "A CNN headline, a flight to Kenya, and a breakfast conversation that changed how I think about extinction.",
  seoDescription:
    "In 2022 I travelled to Ol Pejeta Conservancy in Kenya to meet Najin and Fatu — the last two northern white rhinos on Earth. A long-form photo story by CJ Jayamaha on extinction, conservation, and hope.",
  lead:
    "In January 2022, I came across a CNN article that stopped me in my tracks.",
  quote: "They fucking grow back?",
  tags: [
    "northern white rhino",
    "wildlife conservation",
    "Ol Pejeta",
    "Kenya",
    "photo story",
    "endangered species",
    "CJ Jayamaha",
  ],
};

export const RHINO_STORY_BODY = [
  {
    type: "lead",
    text: "In January 2022, I came across a CNN article that stopped me in my tracks.",
  },
  p(
    "The article explained that the last male northern white rhino, Sudan, had died in 2018. Only two northern white rhinos remained on Earth, Najin and Fatu, and both were female."
  ),
  p("Soon, there would be none left."),
  p(
    "I remember sitting there staring at the screen, trying to process what I had just read. Here was a subspecies that had survived for thousands of years through changing climates, predators, disease, and countless challenges. Yet within a relatively short period of human history, it had been reduced to just two individuals."
  ),
  p("If you’re feeling a little melancholy reading that, that’s exactly how I felt."),
  p("So I made a decision."),
  p("I was going to go and see them."),
  fig("cjmeister-com-_CJ_3035.jpg", "open savanna, Ol Pejeta"),
  p("Three weeks later, I found myself in Kenya."),
  fig("cjmeister-com-_CJ_2831.jpg", "on the road into the conservancy"),
  p(
    "My destination was Ol Pejeta Conservancy, home to the last two northern white rhinos on Earth. Visiting them was the first thing on my itinerary. Everything else could wait."
  ),
  pair(
    "cjmeister-com-_CJ_2850.jpg",
    "cjmeister-com-_CJ_2887.jpg",
    "the long drive in"
  ),
  fig("cjmeister-com-_CJ_2865.jpg", "engage 4x4 low range :-)"),
  p(
    "By pure luck, I was the only guest staying at camp during that period. That gave me the opportunity to spend a great deal of time with my guides, John and Noah. Every morning, before sunrise, we would head out into the conservancy looking for wildlife. Breakfast was often served somewhere in the bush, surrounded by open plains and the sounds of Africa slowly waking up around us."
  ),
  fig("cjmeister-com-_CJ_3007.jpg", "John and Noah on the road"),
  p(
    "The day after I met Najin and Fatu, the three of us sat down for breakfast in the wild and began talking about rhinos. What started as a casual conversation turned into one of the most fascinating discussions of the entire trip."
  ),
  pair(
    "cjmeister-com-_CJ_2903.jpg",
    "cjmeister-com-_CJ_2825.jpg",
    "John and Noah, Ol Pejeta"
  ),
  p(
    "Like many people, I knew there were white rhinos and black rhinos. What I didn’t realise was how much more complicated the story was."
  ),
  p(
    "As John and Noah explained, there are two main rhino species in Africa: the white rhino and the black rhino. The white rhino itself is divided into two subspecies: the southern white rhino and the northern white rhino."
  ),
  p("The southern white rhino has become one of conservation’s success stories."),
  p("The northern white rhino has become one of its greatest tragedies."),
  fig("cjmeister-com-_CJ_1633.jpg", "grazing at Ol Pejeta"),
  p(
    "Standing beside Najin and Fatu the previous day, I had been looking at the final two members of an entire subspecies."
  ),
  p("Nothing quite prepares you for that."),
  p(
    "When I finally met them, they appeared calm, peaceful, and completely unaware of the significance humans had attached to them. To them, it was simply another day. They grazed, wandered about, and occasionally investigated their visitors with curiosity."
  ),
  fig("cjmeister-com-_CJ_1723.jpg", "Najin at Ol Pejeta"),
  fig("cjmeister-com-_CJ_1552.jpg", "she was so close that I could touch her"),
  fig("cjmeister-com-_CJ_1741.jpg", "weathered skin, a lifetime on the plains"),
  p("To everyone around them, however, they represented something much larger."),
  p("These were not simply two rhinos."),
  quote("They were the final chapter of a lineage stretching back thousands of years."),
  p(
    "Every photograph I took felt different from any wildlife photograph I had taken before. There was an awareness that I was documenting something that future generations may never have the opportunity to see for themselves."
  ),
  fig("cjmeister-com-_CJ_2183.jpg", "at rest"),
  p(
    "As our breakfast conversation continued, John explained how some of the last northern white rhinos had been rescued from Central Africa and moved to the Czech Republic. Had that not happened, the northern white rhino might have disappeared decades ago."
  ),
  p("The move effectively bought the subspecies time."),
  p(
    "However, conservationists eventually realised that the animals were struggling in Europe and that if they were to have any chance of reproducing naturally, they needed to return to Africa. In 2009, several northern white rhinos were transported back to Kenya and placed in the safety of Ol Pejeta Conservancy."
  ),
  fig(
    "cjmeister-com-_CJ_2203.jpg",
    "this is a Southern White rhino not threatened; might help us bring back the northern"
  ),
  p(
    "Listening to the story unfold, it became clear just how many people across different countries had contributed to keeping these animals alive. Their survival had depended on conservationists, scientists, veterinarians, governments, and ordinary people making difficult decisions over many decades."
  ),
  fig("cjmeister-com-_CJ_2102.jpg", "John"),
  p(
    "The conversation eventually turned to the reason the northern white rhino had declined in the first place."
  ),
  p(
    "Civil wars, instability, and poaching had devastated populations across parts of Central Africa. Rhino horns were fetching enormous sums of money on international markets, particularly in parts of Asia. The animals became victims of forces they could never understand. Their horns funded conflict, corruption, and criminal networks while pushing the species ever closer to extinction."
  ),
  fig("cjmeister-com-_CJ_2837.jpg", "heading out with the guides"),
  p("The tragedy is not simply that rhinos were being killed."),
  p(
    "It is that they were being killed for something that, as I was about to learn, grows back."
  ),
  p("This was the moment in the conversation that genuinely stopped me."),
  p(
    "John casually mentioned that rhino horns continue growing throughout the animal’s life. They are made primarily of keratin, the same material found in our hair and fingernails. Conservationists sometimes trim them during translocations, and over time the horn simply grows back."
  ),
  p("I stared at him for a second before blurting out my response."),
  quote("They fucking grow back?"),
  fig("cjmeister-com-_CJ_1839.jpg", "at rest"),
  p("The words came out before I had a chance to think."),
  p(
    "I had watched documentaries. I had read articles. I had followed conservation stories for years. Yet somehow nobody had ever clearly explained that rhino horns regrow."
  ),
  p("The fact seemed so obvious once it had been said."),
  p("And the more I thought about it, the more uncomfortable it made me feel."),
  p("If the horn grows back, why kill the animal?"),
  p("Why not remove the horn and allow the rhino to live?"),
  p(
    "Why drive an entire subspecies towards extinction for something nature is perfectly capable of replacing?"
  ),
  p("Of course, the answer is the same answer that sits behind many of humanity’s problems."),
  p("Because killing is easier."),
  p("It is faster."),
  p("It is cheaper."),
  p("It requires less effort, less protection, and less long term thinking."),
  p(
    "Sitting there in the Kenyan wilderness, the conversation stopped being about rhinos for me. It became a reflection on humanity itself."
  ),
  p(
    "Again and again, we choose the immediate benefit over the long term consequence. We take what we want today and leave somebody else to deal with the cost tomorrow."
  ),
  p(
    "The northern white rhino is an extreme example of that behaviour, but it is far from the only one."
  ),
  p(
    "Looking back, I think that was the moment that affected me most during the entire trip."
  ),
  p("Not seeing the last two northern white rhinos."),
  p("Not touching them."),
  p("Not photographing them."),
  p(
    "Learning that the thing people were killing them for grows back anyway."
  ),
  p(
    "That realisation made the story feel even more tragic than it already was."
  ),
  p(
    "The conversation then shifted towards the differences between the various rhino species."
  ),
  p(
    "The day before, I had spent time with both black and white rhinos. At first glance, many people would struggle to tell them apart. Once John and Noah pointed out the differences, however, they became obvious."
  ),
  p(
    "Black rhinos are smaller and have a pointed upper lip designed for browsing shrubs and trees."
  ),
  pair(
    "cjmeister-com-_CJ_2979.jpg",
    "cjmeister-com-_CJ_2940.jpg",
    "early mornings in the bush"
  ),
  p(
    "White rhinos are enormous by comparison, with broad square mouths designed for grazing. Standing next to a white rhino, the sheer scale of the animal is difficult to comprehend. Everything about it feels oversized. The body, the head, the shoulders, the horns."
  ),
  p("The differences between northern and southern white rhinos are more subtle."),
  p(
    "According to John and Noah, northern white rhinos tend to have rougher skin, a more sloping back, and distinctive hair around their ears. Southern white rhinos generally appear more muscular around the neck and have smoother skin."
  ),
  p(
    "These are details that would be easy to miss unless somebody knowledgeable pointed them out."
  ),
  p(
    "And yet those tiny details are now all that separate the last two northern white rhinos from every other white rhino on Earth."
  ),
  p("Eventually our conversation turned towards the future."),
  p(
    "For the first time that morning, the story became less about extinction and more about hope."
  ),
  p(
    "Scientists have preserved semen from several northern white rhino males. Eggs have been collected from both Najin and Fatu. Researchers are creating embryos using northern white rhino genetic material and implanting those embryos into southern white rhino females, which act as surrogate mothers."
  ),
  p("If successful, the resulting calf would not be a hybrid."),
  p("It would be a true northern white rhino."),
  p(
    "The reason for this extraordinary approach is heartbreaking in itself. Najin suffers from leg problems that make carrying a calf for the eighteen month gestation period extremely risky. Fatu has reproductive issues that prevent her from carrying a pregnancy."
  ),
  p(
    "The science exists because the animals themselves can no longer save their own subspecies."
  ),
  p("Standing beside them the previous day had filled me with sadness."),
  p("Listening to the science behind the recovery efforts introduced something else."),
  quote("Not certainty. Not guarantees. But hope."),
  p("Looking back, that is what I remember most about my visit to Kenya."),
  p("Not just seeing the last two northern white rhinos."),
  p("Not just touching them."),
  p("Not just taking photographs that may one day document a chapter of history."),
  p(
    "What I remember most is the contradiction at the heart of their story."
  ),
  p(
    "The northern white rhino represents some of the worst aspects of humanity. Greed. Conflict. Short term thinking. The destruction of something irreplaceable for immediate gain."
  ),
  p("But it also represents some of our best qualities."),
  p(
    "Around the world, scientists continue searching for solutions. Rangers dedicate their lives to protecting wildlife. Conservationists raise awareness and funding. People like John and Noah spend their lives helping visitors understand why these animals matter."
  ),
  p(
    "The same species capable of driving a subspecies toward extinction is also capable of dedicating decades of effort to bringing it back."
  ),
  p("When I left Ol Pejeta, I carried both emotions with me."),
  p("Sadness for what had been lost."),
  p("Hope for what might still be saved."),
  VISIT_COLLAGE,
  p(
    "The video accompanying this article contains photographs from my visit and the breakfast conversation with John and Noah that inspired much of what you’ve just read. It is not a polished interview or a documentary narration. It is simply three people sitting in the Kenyan wilderness talking about rhinos, conservation, and the uncertain future of one of the most remarkable animals on Earth."
  ),
  yt(
    "WmPgNHQBPww",
    "Breakfast in the bush with John and Noah — rhinos, conservation, and what comes next"
  ),
  p(
    "When I look back on my life years from now, I have no doubt this experience will still stand out. Standing face to face with the last two northern white rhinos on Earth, touching them, photographing them, and listening to the people who have dedicated their lives to protecting them was far more than a wildlife encounter. It was a rare opportunity to witness a moment in history that may never exist again."
  ),
  p(
    "I’ve been fortunate enough to travel to many places and experience many incredible things, but this will always be one of the highlights of my life. Not because I saw something rare, but because it reminded me how fragile the natural world really is, and how much of it depends on the choices we make as human beings."
  ),
  p(
    "Long after I have forgotten the details of the journey, I suspect I will still remember standing beside Najin and Fatu, knowing that I was looking at the last two northern white rhinos on Earth."
  ),
];
